import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { UserNumberMessageInterface } from "src/store/callrecorder/userNumberMessagesSlice";
import { fetchConversations, fetchConversationMessages, ConversationItem } from "src/actions/callrecorder/userNumberMessagesActions";
import { formattedDate } from "src/helpers/DateHelpers";
import { queryParams } from "src/helpers/UrlHelpers";
import Pagination from "src/componets/Pagination";
import { Tooltip } from "antd";

import './_Conversation.scss';

interface ConversationInterface {
  userNumberId: string | undefined;
  limit?: number;
}

const formatTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

const formatDateSeparator = (dateStr: string): string => {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';

  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]}`;
};

const STATUS_CONFIG: Record<string, { symbol: string; className: string }> = {
  accepted:     { symbol: '✓',  className: '' },
  queued:       { symbol: '◷',  className: '' },
  sending:      { symbol: '◷',  className: '' },
  sent:         { symbol: '✓',  className: '' },
  receiving:    { symbol: '✓✓', className: '' },
  received:     { symbol: '✓✓', className: '' },
  delivered:    { symbol: '✓✓', className: 'sms-conversation__status--delivered' },
  undelivered:  { symbol: '!',  className: 'sms-conversation__status--failed' },
  failed:       { symbol: '!',  className: 'sms-conversation__status--failed' },
  scheduled:    { symbol: '◷',  className: '' },
  read:         { symbol: '✓✓', className: 'sms-conversation__status--read' },
  canceled:     { symbol: '✕',  className: 'sms-conversation__status--failed' },
};

const groupMessagesByDate = (messages: UserNumberMessageInterface[]) => {
  const groups: { dateKey: string; label: string; messages: UserNumberMessageInterface[] }[] = [];

  messages.forEach(msg => {
    const dateKey = new Date(msg.created_at).toDateString();
    const lastGroup = groups[groups.length - 1];

    if (lastGroup && lastGroup.dateKey === dateKey) {
      lastGroup.messages.push(msg);
    } else {
      groups.push({ dateKey, label: formatDateSeparator(msg.created_at), messages: [msg] });
    }
  });

  return groups;
};

const _Conversation = ({ userNumberId, limit = 50 }: ConversationInterface) => {
  const [searchParams] = useSearchParams();
  const page = queryParams.getInt(searchParams, 'page') || 1;
  const dispatch = useAppDispatch();
  const ids = useAppSelector(state => state.callrecorder.userNumberMessages.ids);
  const entities = useAppSelector(state => state.callrecorder.userNumberMessages.entities);
  const count = useAppSelector(state => state.callrecorder.userNumberMessages.count);
  const status = useAppSelector(state => state.callrecorder.userNumberMessages.status);
  const conversations = useAppSelector(state => state.callrecorder.userNumberMessages.conversations);
  const conversationsStatus = useAppSelector(state => state.callrecorder.userNumberMessages.conversationsStatus);

  const [selectedConversation, setSelectedConversation] = useState<ConversationItem | null>(null);

  // Fetch conversations list
  useEffect(() => {
    if (userNumberId) {
      dispatch(fetchConversations(userNumberId));
    }
  }, [userNumberId, dispatch]);

  // Fetch messages when a conversation is selected
  const offset = (page - 1) * limit;
  const messagesParams = useMemo(() => ({
    limit,
    offset,
    order: 'desc',
    order_by: 'created_at',
  }), [limit, offset]);

  useEffect(() => {
    if (selectedConversation) {
      dispatch(fetchConversationMessages({ conversationId: selectedConversation.id, params: messagesParams }));
    }
  }, [messagesParams, selectedConversation, dispatch]);

  const messages: UserNumberMessageInterface[] = ids
    ? ids.map(id => entities[id]).filter(Boolean) as UserNumberMessageInterface[]
    : [];

  const renderPhone = (headerContent: React.ReactNode, bodyContent: React.ReactNode) => (
    <div className="iphone-mockup">
      <div className="iphone-mockup__frame">
        <div className="iphone-mockup__notch" />
        <div className="iphone-mockup__screen">
          <div className="iphone-mockup__status-bar">
            <span className="iphone-mockup__status-bar-left">9:41</span>
            <span className="iphone-mockup__status-bar-right">5G</span>
          </div>
          <div className="iphone-mockup__header">
            {headerContent}
          </div>
          {bodyContent}
          <div className="iphone-mockup__home-bar">
            <div className="iphone-mockup__home-bar-indicator" />
          </div>
        </div>
      </div>
    </div>
  );

  // Contacts list view
  if (!selectedConversation) {
    const contactsHeader = (
      <div>
        <div className="iphone-mockup__header-title">Messages</div>
        <div className="iphone-mockup__header-subtitle">{conversations.length} conversations</div>
      </div>
    );

    let contactsBody;
    if (conversationsStatus === 'loading') {
      contactsBody = (
        <div className="sms-contacts">
          <div className="sms-contacts__loading">Loading conversations...</div>
        </div>
      );
    } else if (conversations.length === 0) {
      contactsBody = (
        <div className="sms-contacts">
          <div className="sms-contacts__empty">No conversations</div>
        </div>
      );
    } else {
      contactsBody = (
        <div className="sms-contacts">
          {conversations.map((conv) => (
            <div
              key={conv.id}
              className="sms-contacts__item"
              onClick={() => setSelectedConversation(conv)}
            >
              <div className="sms-contacts__avatar">
                {conv.contact_number.slice(-2)}
              </div>
              <div className="sms-contacts__info">
                <div className="sms-contacts__top-row">
                  <div className={`sms-contacts__number ${conv.unread_count > 0 ? 'sms-contacts__number--unread' : ''}`}>
                    {conv.contact_number}
                  </div>
                  <div className="sms-contacts__date">
                    {formattedDate(conv.last_message_at).date}
                  </div>
                </div>
                <div className="sms-contacts__bottom-row">
                  <div className="sms-contacts__preview">
                    {conv.last_message_body}
                  </div>
                  {conv.unread_count > 0 && (
                    <div className="sms-contacts__badge">{conv.unread_count}</div>
                  )}
                </div>
              </div>
              <div className="sms-contacts__chevron">›</div>
            </div>
          ))}
        </div>
      );
    }

    return renderPhone(contactsHeader, contactsBody);
  }

  // Conversation view
  const conversationHeader = (
    <>
      <span className="iphone-mockup__header-back" onClick={() => setSelectedConversation(null)}>
        ‹ Back
      </span>
      <div>
        <div className="iphone-mockup__header-title">{selectedConversation.contact_number}</div>
        <div className="iphone-mockup__header-subtitle">{count} messages</div>
      </div>
    </>
  );

  const dateGroups = groupMessagesByDate(messages);

  let conversationBody;
  if (status === 'loading') {
    conversationBody = (
      <div className="sms-conversation">
        <div className="sms-conversation__loading">Loading messages...</div>
      </div>
    );
  } else if (messages.length === 0 && status === 'succeeded') {
    conversationBody = (
      <div className="sms-conversation">
        <div className="sms-conversation__empty">No messages</div>
      </div>
    );
  } else {
    conversationBody = (
      <div className="sms-conversation">
        {dateGroups.map((group) => (
          <React.Fragment key={group.dateKey}>
            {group.messages.map((msg) => {
              const isOutbound = msg.direction === 'outbound';
              const statusInfo = msg.status ? STATUS_CONFIG[msg.status] : null;
              return (
                <div
                  key={msg.id}
                  className={`sms-conversation__row ${isOutbound ? 'sms-conversation__row--outbound' : 'sms-conversation__row--inbound'}`}
                >
                  <div className={`sms-conversation__bubble ${isOutbound ? 'sms-conversation__bubble--outbound' : 'sms-conversation__bubble--inbound'}`}>
                    <div className="sms-conversation__body">{msg.body}</div>
                    <div className="sms-conversation__meta">
                      <span className="sms-conversation__time">{formatTime(msg.created_at)}</span>
                      {isOutbound && statusInfo && (
                        <Tooltip title={msg.status}>
                          <span className={`sms-conversation__status ${statusInfo.className}`}>{statusInfo.symbol}</span>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="sms-conversation__date-separator">
              <span>{group.label}</span>
            </div>
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div>
      {renderPhone(conversationHeader, conversationBody)}
      <Pagination className='v-mt-10' total={count} size={limit} />
    </div>
  );
}

export default _Conversation;
